import { getLocaleString } from '@assets/i18n/config';
import {
  CountryCodes,
  Jurisdictions,
  LanguageCodes,
} from '@onevesthq/ov-enums';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { getCountryName } from '@ovComponents/resources';
import {
  formatAmountValueCents,
  generateAddressString,
  generateAlternativeAddressString,
} from '@utils/common';
import dayjs from 'dayjs';
import { z } from 'zod/v3';
import {
  formatWorkflowReviewPhoneDisplay,
  isWorkflowReviewPhoneFieldKey,
  looksLikeNorthAmericanPhoneDigits,
} from './workflowReviewPhoneDisplay';

/** A heuristic to check if a value looks like formatted object output (has newlines or "Key: value" pattern) */
const isObjectOutput = (v: string | undefined): v is string =>
  !!v && (v.includes('\n') || /^[A-Z][a-zA-Z0-9]*: /.test(v));

const isCountryLikeFieldKey = (fieldKey: string): boolean => {
  const normalizedFieldKey = fieldKey.toLowerCase();
  return (
    normalizedFieldKey.includes('citizenship') ||
    normalizedFieldKey.includes('country')
  );
};

const PrimitiveSchema = (
  displayTextForValue: DisplayTextForValue,
  fieldKey: string,
) =>
  z.union([
    z
      .string()
      .refine((v) => !!displayTextForValue[v])
      .transform(
        (v: keyof typeof displayTextForValue) => displayTextForValue[v],
      ),
    z
      .string()
      .datetime()
      .transform((v) => dayjs(v).format('MMM Do, YYYY')),
    z.boolean().transform((isTrue) => (isTrue ? 'Yes' : 'No')),
    z.literal('true').transform(() => 'Yes'),
    z.literal('false').transform(() => 'No'),
    z
      .union([
        z.number(),
        z.bigint(),
        z.string().refine((str) => !Number.isNaN(Number(str))),
      ])
      .refine(() => fieldKey.includes('Cents'))
      .transform((num) => {
        const formattedValue = formatAmountValueCents(Number(num));
        return getLocaleString().startsWith('en')
          ? `$${formattedValue}`
          : `${formattedValue} $`;
      }),
    z
      .union([
        z.number(),
        z.bigint(),
        z.string().refine((str) => !Number.isNaN(Number(str))),
      ])
      .refine(() => isWorkflowReviewPhoneFieldKey(fieldKey))
      .refine((v) => looksLikeNorthAmericanPhoneDigits(String(v)))
      .transform((v) => formatWorkflowReviewPhoneDisplay(String(v))),
    z.number().transform(String),
    z // enum values
      .string()
      .refine((v) => /^[A-Z0-9_]+$/.test(v) && !displayTextForValue[v])
      .transform((v) => {
        if (isCountryLikeFieldKey(fieldKey)) {
          const countryName = getCountryName(v.toUpperCase());
          if (countryName) {
            return countryName;
          }
        }
        return v
          .split('_')
          .map(
            (part) =>
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
          )
          .join(' ');
      }),
    z
      .string()
      .refine(() => isWorkflowReviewPhoneFieldKey(fieldKey))
      .transform((v) => formatWorkflowReviewPhoneDisplay(v)),
    z.string(),
    // PhysicalAddress format (streetName + city)
    z
      .object({
        city: z.string(),
        province: z.string().optional(),
        jurisdiction: z.string().optional(),
        streetName: z.string(),
        postal: z.coerce.string().optional(),
        unitNumber: z.coerce.string().optional(),
        houseNumber: z.coerce.string().optional(),
        neighborhood: z.string().optional(),
        country: z.string().optional(),
      })
      .transform((obj) => generateAddressString(obj as any)),
    // AlternativeAddress format (line1 + city)
    z
      .object({
        city: z.string(),
        province: z.string().optional(),
        jurisdiction: z.string().optional(),
        line1: z.coerce.string(),
        line2: z.coerce.string().optional(),
        postal: z.coerce.string().optional(),
        country: z.string().optional(),
      })
      .transform((obj) => generateAlternativeAddressString(obj as any)),
    z.bigint().transform((num) => String(num)),
    z.null().transform(() => ''),
    z.undefined().transform(() => ''),
    z.never().transform(() => ''),
  ]);

const ObjectSchema = (
  displayTextForValue: DisplayTextForValue,
  _fieldKey: string,
): z.ZodType<string, z.ZodTypeDef, unknown> =>
  z
    .record(z.string(), z.unknown())
    .transform((obj) =>
      Object.fromEntries(
        Object.entries(obj)
          .filter(
            ([k]) => k !== '_id' && k !== '__typename' && !k.startsWith('_'),
          )
          .filter(([k, v]) => !(k === 'title' && v === '_')),
      ),
    )
    .transform((obj) =>
      Object.entries(obj)
        .sort()
        .map(([k, v]) => {
          const valueSchema = ChangeValueSchema(displayTextForValue, k);
          const result = valueSchema.safeParse(v);
          const formattedValue = result.success ? result.data : String(v);
          const key = k.charAt(0).toUpperCase() + k.slice(1);
          if (isObjectOutput(formattedValue)) {
            const indented = formattedValue
              .split('\n')
              .map((line) => `\t${line}`)
              .join('\n');
            return `${key}:\n${indented}`;
          }
          return `${key}: ${formattedValue}`;
        })
        .join('\n'),
    );

const ArraySchema = (
  displayTextForValue: DisplayTextForValue,
  fieldKey: string,
): z.ZodType<string, z.ZodTypeDef, unknown> =>
  z
    .array(
      z.union([
        ObjectSchema(displayTextForValue, fieldKey),
        z.lazy(() => ArraySchema(displayTextForValue, fieldKey)),
        PrimitiveSchema(displayTextForValue, fieldKey),
      ]),
    )
    .transform((list) => {
      const hasObjectOutput = list.some(isObjectOutput);
      return hasObjectOutput ? list.join(',\n') : list.join(', ');
    });

export const ChangeValueSchema = (
  displayTextForValue: DisplayTextForValue,
  fieldKey: string,
) =>
  z.union([
    PrimitiveSchema(displayTextForValue, fieldKey),
    ArraySchema(displayTextForValue, fieldKey),
    ObjectSchema(displayTextForValue, fieldKey),
  ]);

// ===== Core Translation Schema =====
// Note: All languages can be null from the API (WorkflowTranslatedString type)
export const translationSchema = z.object(
  Object.fromEntries(
    Object.values(LanguageCodes).map((lang) => [lang, z.string().nullish()]),
  ),
);

// ===== Changes Schema (Main Output) =====
export const changesSchema = z.record(
  z.string(),
  z.object({
    stepName: translationSchema,
    changes: z.record(
      z.string(),
      z.object({
        fields: z.array(
          z.object({
            key: translationSchema,
            oldValue: z.string(),
            newValue: z.string(),
            subStep: z
              .object({
                id: z.string().optional(),
                name: translationSchema.optional(),
              })
              .optional(),
          }),
        ),
        originalAndLastUpdated: z.object({
          key: translationSchema,
          oldValue: z.string(),
          newValue: z.string(),
          subStep: z
            .object({
              id: z.string().optional(),
              name: translationSchema.optional(),
            })
            .optional(),
        }),
      }),
    ),
  }),
);

// ===== Change Pair Schema =====
// A change pair is [oldValue, newValue]
export const changePairSchema = z.tuple([z.unknown(), z.unknown()]);

// ===== Single Change Object Schema (from log.changes) =====
// This is what we iterate over in the for loop
// Each change value can be either a pair [old, new] or a nested object
export const changeObjectSchema = z.record(
  z.string(),
  changePairSchema.or(z.record(z.string(), z.unknown())),
);

export type LabelsByKey = Record<
  string,
  {
    label: z.infer<typeof translationSchema>;
    subStep: {
      id?: string | undefined;
    };
  }
>;

export type DisplayTextForValue = Record<string, string>;

// ===== Validation & Type Guard Functions =====
/**
 * Type guard to check if a value is a change pair [oldValue, newValue]
 */
export function isChangePair(
  value: unknown,
): value is z.infer<typeof changePairSchema> {
  if (!Array.isArray(value)) return false;
  return value.length === 2;
}

/**
 * Safely parses translation object with fallback
 * Dynamically supports all languages defined in LanguageCodes
 */
export function parseTranslation(
  value: unknown,
  fallback: z.infer<typeof translationSchema> = getLanguageDefaults(''),
): z.infer<typeof translationSchema> {
  const result = translationSchema.safeParse(value);
  return result.success ? result.data : fallback;
}

/**
 * Creates a translation object from a string
 * Dynamically supports all languages defined in LanguageCodes
 */
export function createTranslation(
  value: string,
): z.infer<typeof translationSchema> {
  return getLanguageDefaults(value);
}
